import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Person as PersonIcon, Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const EmployeeDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phoneNumber: '',
    alternatePhone: '',
    address: '',
    email: '',
    role: '',
    site: '',
    dateOfJoin: '',
    employeeImage: null
  });

  // Sample salary data
  const salaryData = [
    { month: 'Jan', salary: 30000, advance: 5000, totalAmount: 35000, payment: 'Banking' },
    { month: 'Feb', salary: 30000, advance: 5000, totalAmount: 35000, payment: 'Banking' },
    { month: 'Mar', salary: 30000, advance: '-', totalAmount: 30000, payment: 'Banking' },
    { month: 'Apr', salary: 30000, advance: '-', totalAmount: 3000, payment: 'Banking' },
    { month: 'May', salary: 30000, advance: '-', totalAmount: 30000, payment: 'Banking' },
    { month: 'Jun', salary: 30000, advance: '-', totalAmount: 30000, payment: 'Banking' },
    { month: 'Jul', salary: 30000, advance: '-', totalAmount: 30000, payment: 'Banking' },
    { month: 'Aug', salary: 30000, advance: '-', totalAmount: 30000, payment: 'Banking' }
  ];

  useEffect(() => {
    // Get employee data from location state or use default
    if (location.state?.employee) {
      setEmployee(location.state.employee);
      setEditFormData(location.state.employee);
    } else {
      // Default employee data if no state passed
      const defaultEmployee = {
        id: 'SM001',
        name: 'Selva',
        role: 'Supervisor',
        phoneNumber: '9986568890',
        alternatePhone: '8757568767',
        email: 'selva@gmail.com',
        address: 'Akshya Nagar Guindy',
        site: 'Guindy',
        dateOfJoin: '1 Jan 1998',
        employeeImage: null
      };
      setEmployee(defaultEmployee);
      setEditFormData(defaultEmployee);
    }
  }, [location.state]);

  const handleClose = () => {
    // Navigate back to the previous page or attendance
    navigate(-1);
  };

  const handleEditProfile = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // Reset form data to original employee data
    setEditFormData(employee);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditFormData(prev => ({
          ...prev,
          employeeImage: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = () => {
    // Update employee data
    setEmployee(editFormData);
    setIsEditModalOpen(false);
    console.log('Profile updated:', editFormData);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!employee) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Background overlay */}
      <div 
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Modal content */}
      <div 
        className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-900">Employee Details</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Panel - Employee Profile and Basic Information */}
          <div className="w-full lg:w-2/5 space-y-6">
            {/* Employee Profile Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Green Header Banner */}
              <div className="bg-primary px-6 py-4">
                <div className="flex items-center space-x-4">
                  {/* Employee Image */}
                  <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                    {employee.employeeImage ? (
                      <img
                        src={employee.employeeImage}
                        alt={employee.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <PersonIcon className="w-8 h-8 text-gray-500" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-white">{employee.name}</h1>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="bg-white text-primary px-3 py-1 rounded-full text-sm font-medium">
                        {employee.role}
                      </span>
                    </div>
                  </div>
                  
                  {/* <button
                    onClick={handleEditProfile}
                    className="bg-white  px-4 py-2 rounded-md font-medium  flex items-center space-x-2"
                  >
                    <EditIcon className="w-4 h-4" />
                    <span>Edit Profilew</span>
                  </button> */}
                </div>
              </div>

              {/* Employee Details */}
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Employee ID:</span>
                    <span className="font-medium">{employee.employeeId}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Role:</span>
                    <span className="font-medium">{employee.role}</span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Date Of Join:</span>
                    <span className="font-medium">{employee.dateOfJoin}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{employee.phoneNumber}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600">Alternate No:</span>
                  <span className="font-medium">{employee.alternatePhone}</span>
                </div>
                
                {/* <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600">Site:</span>
                  <span className="font-medium">{employee.site}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600">Address:</span>
                  <span className="font-medium">{employee.address}</span>
                </div> */}
              </div>
            </div>
          </div>

         
          {/* <div className="w-full lg:w-3/5 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Salary Details</h3>
         
              <div className="flex items-center space-x-3">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                </select>
                
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="All">All</option>
                  <option value="Jan">Jan</option>
                  <option value="Feb">Feb</option>
                  <option value="Mar">Mar</option>
                  <option value="Apr">Apr</option>
                  <option value="May">May</option>
                  <option value="Jun">Jun</option>
                  <option value="Jul">Jul</option>
                  <option value="Aug">Aug</option>
                </select>
                
                <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            
             <div className="border border-gray-200 rounded-lg overflow-hidden">
              
               <div className="bg-gray-50 border-b border-gray-200">
                 <div className="grid grid-cols-5 gap-4 py-3 px-4">
                   <div className="text-left font-medium text-gray-700">Month</div>
                   <div className="text-left font-medium text-gray-700">Salary</div>
                   <div className="text-left font-medium text-gray-700">Advance</div>
                   <div className="text-left font-medium text-gray-700">Total Amount</div>
                   <div className="text-left font-medium text-gray-700">Payment</div>
                 </div>
               </div>
               
             
               <div className="max-h-96 overflow-y-auto">
                 {salaryData.map((row, index) => (
                   <div key={index} className="grid grid-cols-5 gap-4 py-3 px-4 border-b border-gray-100 hover:bg-gray-50">
                     <div className="text-gray-900">{row.month}</div>
                     <div className="text-gray-900">{row.salary.toLocaleString()}</div>
                     <div className="text-gray-900">{row.advance}</div>
                     <div className="text-gray-900 font-medium">{row.totalAmount.toLocaleString()}</div>
                     <div className="text-gray-900">{row.payment}</div>
                   </div>
                 ))}
               </div>
             </div>
           </div> */}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseEditModal();
            }
          }}
        >
           <div 
             className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
             onClick={(e) => e.stopPropagation()}
           >
             <div className="p-6">
               <div className="flex justify-between items-center mb-6">
                 <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
                 <button
                   onClick={handleCloseEditModal}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>

               <form className="space-y-6">
                 {/* Profile Image */}
                 <div className="flex items-center space-x-4">
                   <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                     {editFormData.employeeImage ? (
                       <img
                         src={editFormData.employeeImage}
                         alt="Profile"
                         className="w-20 h-20 rounded-full object-cover"
                       />
                     ) : (
                       <PersonIcon className="w-10 h-10 text-gray-500" />
                     )}
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Profile Image
                     </label>
                     <input
                       type="file"
                       accept="image/*"
                       onChange={handleImageChange}
                       className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                     />
                   </div>
                 </div>

                 {/* Basic Information */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Full Name *
                     </label>
                     <input
                       type="text"
                       name="name"
                       value={editFormData.name}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       required
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Employee ID
                     </label>
                     <input
                       type="text"
                       value={employee.id}
                       disabled
                       className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Phone Number *
                     </label>
                     <input
                       type="tel"
                       name="phoneNumber"
                       value={editFormData.phoneNumber}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       required
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Alternate Phone
                     </label>
                     <input
                       type="tel"
                       name="alternatePhone"
                       value={editFormData.alternatePhone}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Email *
                     </label>
                     <input
                       type="email"
                       name="email"
                       value={editFormData.email}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       required
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Role *
                     </label>
                     <select
                       name="role"
                       value={editFormData.role}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       required
                     >
                       <option value="">Select Role</option>
                       <option value="Supervisor">Supervisor</option>
                       <option value="Manager">Manager</option>
                       <option value="Employee">Employee</option>
                       <option value="Admin">Admin</option>
                     </select>
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Site
                     </label>
                     <input
                       type="text"
                       name="site"
                       value={editFormData.site}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Date of Join
                     </label>
                     <input
                       type="date"
                       name="dateOfJoin"
                       value={editFormData.dateOfJoin}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     Address *
                   </label>
                   <textarea
                     name="address"
                     value={editFormData.address}
                     onChange={handleInputChange}
                     rows={3}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>

                 {/* Action Buttons */}
                 <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                   <button
                     type="button"
                     onClick={handleCloseEditModal}
                     className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                   >
                     Cancel
                   </button>
                   <button
                     type="button"
                     onClick={handleSaveProfile}
                     className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     Save Changes
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };
 
 export default EmployeeDetails;
